/**
 * use-job-detail — fetch a single job's full detail (Quick 260614-t4m / #6).
 *
 * GET /v1/jobs/:id (VIEW_RUN_TICKETS). Enabled only when a jobId is selected, so
 * the query does not fire until the detail modal opens.
 */

import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

/** Subset of GET /v1/jobs/:id the dispatcher detail modal renders. */
export interface JobDetail {
  readonly id: string;
  readonly haulId: string | null;
  readonly statusCode: string;
  readonly statusLabel: string;
  readonly jobTypeCode: string;
  readonly jobTypeLabel: string;
  // Deferred item 09.1-09 — the job's CURRENT job_type_id (pre-selects the edit
  // dropdown) + capture-exists flag (gates the dropdown read-only) + the job's
  // customer (hauler_operator_id, scopes the dropdown options). Optional for
  // backward-compat with tests. See routes/jobs/detail.ts.
  readonly jobTypeId?: number;
  readonly hasCapture?: boolean;
  // DEC-355 (OPEN-151 PR 2) — set once the load was voided ("Void load and cancel").
  readonly captureVoidedAt?: string | null;
  readonly captureVoidedReason?: string | null;
  readonly haulerOperatorId?: string | null;
  readonly billingMethod: string | null;
  readonly scheduledAt: string | null;
  readonly completedAt: string | null;
  // Field-test fix #2 — net BBL hauled (reviewed_net_bbl override, else computed);
  // null until the job has a capture. Optional for backward-compat with tests.
  readonly netBbl?: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly siteId: string | null;
  // DEC-166 — the standalone-well pickup id (peer of siteId; set when siteId /
  // hauler_location_id is NULL). Mirrors the list response so a detail consumer
  // can distinguish a well pickup from a tank battery. Optional for backward-compat
  // with tests. See routes/jobs/detail.ts.
  readonly pickupWellId?: string | null;
  readonly siteName: string | null;
  // #60 (DEC-365) — the well on the location the load came from (null = not recorded).
  readonly sourceWellId?: string | null;
  readonly sourceWellName?: string | null;
  // #60 (DEC-365) — what the latest site change did to the run ticket's tank links; null when
  // the site never changed or nothing is left to pick.
  readonly siteChangeTankOutcome?: {
    readonly tankRelinks: ReadonlyArray<{
      readonly tankName: string;
      readonly fromTankId: string;
      readonly toTankId: string;
    }>;
    readonly unlinkedTankNames: ReadonlyArray<string>;
  } | null;
  readonly apiNumber: string | null;
  readonly siteState: string | null;
  readonly operatorId: string | null;
  readonly operatorName: string | null;
  readonly driverId: string | null;
  readonly driverName: string | null;
  readonly dispatcherName: string | null;
  readonly createdByName: string | null;
  readonly dispatcherNotes: string | null;
  /**
   * DEC-299 / Task 18.6 — the OPERATOR's note on a haul request made through the public
   * API (jobs.operator_request_notes), verbatim. Never merged into dispatcherNotes: it is
   * rendered by OperatorRequestNote, labelled as the operator's words. Null for every job a
   * dispatcher, driver or foreman created. Optional so existing literals compile.
   */
  readonly operatorRequestNotes?: string | null;
  // Legacy "New Request" parity — requester contact fields (read + edit).
  // Optional for backward-compat with tests. See routes/jobs/detail.ts.
  readonly requesterName?: string | null;
  readonly requesterPhone?: string | null;
  readonly requesterEmail?: string | null;
  // DEC-146 (Phase 17) — urgent flag.
  readonly isUrgent?: boolean;
  // OPEN-117 — the board's "Not started" (api services/job-not-started.ts). A plain
  // dispatcher may cancel the job while it is true.
  readonly notStarted?: boolean;
  // DEC-355 (OPEN-151) — "Send to review" from the detail: null unless the job is in progress
  // (and never for a driver). onStuckList false = the driver was active recently (warn).
  readonly sendToReview?: {
    readonly hasCapturedLoad: boolean;
    readonly lastActivityAt: string;
    readonly onStuckList: boolean;
  } | null;
  readonly driverNotes: string | null;
  // 260624-job-destination — planned disposal facility (nested object from
  // detail.ts; null when unassigned). Optional for backward-compat with tests.
  readonly plannedDisposalFacility?: { readonly id: string; readonly displayName: string } | null;
  readonly actualDisposalFacility?: { readonly id: string; readonly displayName: string } | null;
  // Phase 26.3 / SWD A1 (0210) — the other two dropoff kinds, planned and actual.
  readonly plannedDropoffHaulerLocation?: { readonly id: string; readonly name: string } | null;
  readonly actualDropoffHaulerLocation?: { readonly id: string; readonly name: string } | null;
  readonly plannedDropoffDisposalWell?: { readonly id: string; readonly name: string } | null;
  readonly actualDropoffDisposalWell?: { readonly id: string; readonly name: string } | null;
  /** SWD A1 — where the load goes now: the actual target if any, else the planned one. */
  readonly currentDropoff?: {
    readonly kind: 'DISPOSAL_FACILITY' | 'HAULER_LOCATION' | 'DISPOSAL_WELL';
    readonly id: string;
    readonly name: string;
    readonly isActual: boolean;
    /** DISPOSAL_WELL only: the catalog well (wells.id), which its place note is keyed by. */
    readonly wellId?: string | null;
  } | null;
  // OPEN-153 / DEC-354 — total paused time, derived from the event chain on every read.
  // Optional for backward-compat with tests. See routes/jobs/detail.ts.
  readonly pausedSeconds?: number;
  readonly pauseCount?: number;
  /** OPEN-144 PR 3 — open parked second run tickets (null for a driver). */
  readonly openParkedCount?: number | null;
}

interface JobDetailResponse {
  readonly job: JobDetail;
}

/** Query key for a single job detail. */
export const JOB_DETAIL_QUERY_KEY = (jobId: string): readonly [string, string] =>
  ['job-detail', jobId] as const;

/**
 * Fetches a single job's detail. Pass `null` to disable (modal closed).
 */
export function useJobDetail(
  apiOpts: ReturnType<typeof useApiOpts>,
  jobId: string | null,
): ReturnType<typeof useApiQuery<JobDetailResponse>> {
  return useApiQuery<JobDetailResponse>(apiOpts, `/v1/jobs/${jobId ?? ''}`, {
    queryKey: JOB_DETAIL_QUERY_KEY(jobId ?? '__none__'),
    enabled: jobId !== null,
    staleTime: 10_000,
  });
}
